import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getEquipmentExpenseDetails } from '../../../../redux/Slices/EquipmentExpensesSlice';

const ViewEquipmentExpenses = () => {
  const { t } = useTranslation();
  const { equipmentId, expenseId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(
          getEquipmentExpenseDetails({ equipmentId, expenseId })
        ).unwrap();
        setDetails(payload?.data || payload);
      } catch (error) {
        toast.error(error || t('equipmentExpenses.view.messages.fetchError'), { rtl: isRTL });
        navigate('/equipment-expenses');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, equipmentId, expenseId, navigate, t, isRTL]);


  const formatDate = (value) => {
    if (!value) return '—';
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('en-US');
    } catch {
      return value;
    }
  };

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.view.loading')}</div>
      </div>
    );
  }

  if (!details) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.view.noData')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, direction: isRTL ? 'rtl' : 'ltr' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>{t('equipmentExpenses.view.title')}</h2>
          <button style={secondaryButtonStyle} onClick={() => navigate('/equipment-expenses')}>
            {t('equipmentExpenses.view.back')}
          </button>
        </div>
        <div style={detailsGrid}>
          <InfoRow label={t('equipmentExpenses.view.expenseNumber')} value={details.id || '—'} isRTL={isRTL} />
          <InfoRow 
            label={t('equipmentExpenses.view.equipment')} 
            value={isRTL 
              ? (details.equipment?.name_ar || details.equipment?.name_en || '—')
              : (details.equipment?.name_en || details.equipment?.name_ar || '—')} 
            isRTL={isRTL} 
          />
          <InfoRow label={t('equipmentExpenses.view.equipmentId')} value={details.equipment_id || '—'} isRTL={isRTL} />
          <InfoRow label={t('equipmentExpenses.view.expenseDate')} value={formatDate(details.expense_date)} isRTL={isRTL} />
          <InfoRow label={t('equipmentExpenses.view.amount')} value={details.amount ? `${details.amount}` : '—'} isRTL={isRTL} />
          {isRTL ? (
            <>
              <InfoRow label={t('equipmentExpenses.view.descriptionAr')} value={details.description_ar || '—'} isRTL={true} />
              <InfoRow label={t('equipmentExpenses.view.descriptionEn')} value={details.description_en || '—'} isRTL={true} />
            </>
          ) : (
            <>
              <InfoRow label={t('equipmentExpenses.view.descriptionEn')} value={details.description_en || '—'} isRTL={false} />
              <InfoRow label={t('equipmentExpenses.view.descriptionAr')} value={details.description_ar || '—'} isRTL={false} />
            </>
          )}
          <InfoRow 
            label={t('equipmentExpenses.view.project')} 
            value={isRTL
              ? (details.project?.name_ar || details.project?.name_en || '—')
              : (details.project?.name_en || details.project?.name_ar || '—')} 
            isRTL={isRTL} 
          />
          <InfoRow 
            label={t('equipmentExpenses.view.paymentAccount')} 
            value={isRTL
              ? (details.payment_account?.name_ar || details.payment_account?.name_en || (details.payment_account_id ? `#${details.payment_account_id}` : '—'))
              : (details.payment_account?.name_en || details.payment_account?.name_ar || (details.payment_account_id ? `#${details.payment_account_id}` : '—'))} 
            isRTL={isRTL} 
          />
          <InfoRow label={t('equipmentExpenses.view.paymentAccountId')} value={details.payment_account_id || '—'} isRTL={isRTL} />
          <InfoRow label={t('equipmentExpenses.view.journalEntryId')} value={details.journal_entry_id || '—'} isRTL={isRTL} />
          <InfoRow label={t('equipmentExpenses.view.notes')} value={details.notes || '—'} isRTL={isRTL} />
          <InfoRow label={t('equipmentExpenses.view.createdAt')} value={formatDate(details.created_at)} isRTL={isRTL} />
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, isRTL }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
    <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 'bold' }}>{value || '—'}</span>
  </div>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 700,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};

const detailsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '10px 18px',
  borderRadius: 8,
  cursor: 'pointer',
};

export default ViewEquipmentExpenses;
